import {
  Filter,
  type SelectItem,
  useSelectQueryGate,
} from '@vrplatform/components';
import { useAccountsQuery, useTeam } from '@vrplatform/data';
import type { accountClassification_enum } from '@vrplatform/graphql';
import {
  hasValue,
  sortBy,
  toTitleCase,
  uniqueBy,
  type Maybe,
} from '@vrplatform/utils';
import type { BaseCustomFilterProps } from './_types';
import { useMemo } from 'react';

type Single = {
  type: 'single';
  classification: Maybe<accountClassification_enum>;
  setClassification: (
    classification: accountClassification_enum | null
  ) => void;
};

type Multiple = {
  type: 'multiple';
  classification: Maybe<accountClassification_enum[]>;
  setClassification: (
    classifications: accountClassification_enum[] | null
  ) => void;
};

type Props = BaseCustomFilterProps & (Single | Multiple);

function useClassificationQuery({ skip }: { skip?: boolean } = {}) {
  const [{ id: teamId }] = useTeam();

  const queryData = useAccountsQuery({
    skip: skip || !teamId,
  });

  const data = useMemo<SelectItem<accountClassification_enum>[]>(() => {
    const initial = (
      queryData.data?.map((entry) => entry.category?.classification) ?? []
    ).filter(hasValue);

    const classifications = uniqueBy(initial);

    return sortBy(
      classifications.map<SelectItem<accountClassification_enum>>(
        (classification) => ({
          value: classification,
          label: toTitleCase(classification),
        })
      ),
      'label'
    );
  }, [queryData.data]);

  return {
    ...queryData,
    data,
  };
}

export const ClassificationFilter = ({
  label = 'Classification',
  ...props
}: Props) => {
  if (props.type === 'single') {
    return <SingleInput {...props} label={label} />;
  }

  return <MultipleInput {...props} label={label} />;
};

type Base = {
  label: string;
};

const SingleInput = ({
  classification,
  setClassification,
  label,
}: Base & Single) => {
  const gate = useSelectQueryGate({ value: classification });
  const queryData = useClassificationQuery({
    skip: !gate.shouldFetchOptions,
  });

  const options = queryData.data || [];

  const value = classification
    ? {
        value: classification,
        label: toTitleCase(classification),
      }
    : null;

  return (
    <Filter.Select
      type="single"
      label={label}
      value={value}
      data={{
        options,
        error: queryData.error,
        loading: queryData.isLoading,
      }}
      setValue={(value) => setClassification(value?.value || null)}
      hideSearch
      onOpenChange={gate.onOpenChange}
    />
  );
};

const MultipleInput = ({
  classification,
  setClassification,
  label,
}: Base & Multiple) => {
  const gate = useSelectQueryGate({ value: classification });
  const queryData = useClassificationQuery({
    skip: !gate.shouldFetchOptions,
  });

  const options = queryData.data || [];
  const value =
    classification?.map((entry) => ({
      value: entry,
      label: toTitleCase(entry),
    })) || [];

  return (
    <Filter.Select
      type="multiple"
      label={label}
      value={value}
      data={{
        options,
        error: queryData.error,
        loading: queryData.isLoading,
      }}
      setValue={(next) =>
        setClassification(next.length ? next.map((entry) => entry.value) : null)
      }
      hideSearch
      onOpenChange={gate.onOpenChange}
    />
  );
};
